import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { subscribeWorkspaceArchived } from "../../utils/archivedWorkspace";

// A slow request fired before the archive can 410 well after the first burst's
// cycle already completed (fetched + navigated) — the in-flight guard alone
// only dedupes notifications that arrive WHILE fetchWorkspaces() is pending.
// Treat any straggler within this window of the last completed cycle as part
// of the same event instead of re-running the toast/fetch/navigate and
// yanking the user back to the workspace it picked, even if they've moved on.
const STRAGGLER_WINDOW_MS = 10_000;

// Every in-flight request on the archived workspace 410s at once, so collapse
// the burst into a single refetch + redirect.
const WorkspaceArchivedListener = () => {
  const { fetchWorkspaces } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const handling = useRef(false);
  const lastHandledAt = useRef(0);

  useEffect(
    () =>
      subscribeWorkspaceArchived(async () => {
        if (handling.current) return;
        if (Date.now() - lastHandledAt.current < STRAGGLER_WINDOW_MS) return;
        handling.current = true;
        try {
          showToast("This workspace was archived", "info");
          const list = (await fetchWorkspaces()) || [];
          navigate(list.length ? `/${list[0].uuid}/dashboard` : "/", { replace: true });
        } finally {
          handling.current = false;
          lastHandledAt.current = Date.now();
        }
      }),
    [fetchWorkspaces, showToast, navigate],
  );

  return null;
};

export default WorkspaceArchivedListener;
